<script setup lang="ts">
import { useFairSheetSwipe } from './useFairSheetSwipe'
/**
 * DS Modal panel — Large overlays › Modal (324:6670).
 * Size=Default (324:4917): 440 wide, 16 in, radius/card, effect `Modal`,
 * header · hairline · content (16 / 24) · footer, 8 between.
 * Size=Mobile (324:6671): a bottom sheet — grabber, centred title row,
 * content 16 / 32, 48 tall buttons.
 *
 * With `showHeader` off the default slot is the whole body (consumers that
 * draw their own header); with it on the slot sits in the padded content.
 */
import { FairIconButton } from '@/ui/primitives/FairIconButton'
import FairDialogContent from './FairDialogContent.vue'
import FairDialogFooter from './FairDialogFooter.vue'
import FairDialogHeader from './FairDialogHeader.vue'
import type { FairIconName } from '@/ui/tokens/icons.data'

interface FairDialogPanelProps {
  header?: string
  closable?: boolean
  showHeader?: boolean
  showFooter?: boolean
  showCancel?: boolean
  showBack?: boolean
  cancelLabel?: string
  confirmLabel?: string
  confirmDisabled?: boolean
  confirmLoading?: boolean
  closeLabel?: string
  backLabel?: string
  mobile?: boolean
  cancelLeadingIcon?: FairIconName
  cancelTrailingIcon?: FairIconName
  confirmLeadingIcon?: FairIconName
  confirmTrailingIcon?: FairIconName
  contentText?: string
}

const props = withDefaults(defineProps<FairDialogPanelProps>(), {
  header: 'Header',
  closable: true,
  showHeader: true,
  showFooter: true,
  showCancel: true,
  showBack: false,
  cancelLabel: 'Cancel',
  confirmLabel: 'Confirm',
  confirmDisabled: false,
  confirmLoading: false,
  closeLabel: 'Close',
  backLabel: 'Back',
  mobile: false,
  cancelLeadingIcon: undefined,
  cancelTrailingIcon: undefined,
  confirmLeadingIcon: undefined,
  confirmTrailingIcon: undefined,
  contentText: undefined,
})

const emit = defineEmits<{
  close: []
  back: []
  cancel: [event: MouseEvent]
  confirm: [event: MouseEvent]
}>()

/** Mobile sheet: pull the grabber / header down to close. */
const swipe = useFairSheetSwipe({
  enabled: () => props.mobile && props.closable,
  onDismiss: () => emit('close'),
})
</script>

<template>
  <div
    class="fair-dialog-panel"
    :class="{ 'fair-dialog-panel--mobile': mobile }"
    :style="swipe.style.value"
    role="dialog"
    aria-modal="true"
  >
    <div
      v-if="mobile"
      class="fair-dialog-panel__grabber"
      aria-hidden="true"
      v-on="swipe.handlers"
    />

    <template v-if="showHeader">
      <slot name="header">
        <FairDialogHeader
          :title="header"
          :mobile="mobile"
          v-on="mobile ? swipe.handlers : {}"
          :closable="closable"
          :show-back="showBack"
          :close-label="closeLabel"
          :back-label="backLabel"
          @close="emit('close')"
          @back="emit('back')"
        >
          <template
            v-if="$slots['header-start']"
            #start
          >
            <slot name="header-start" />
          </template>
          <template
            v-if="$slots['header-end']"
            #end
          >
            <slot name="header-end" />
          </template>
        </FairDialogHeader>
      </slot>
      <div
        v-if="!mobile"
        class="fair-dialog-panel__divider"
        aria-hidden="true"
      />
      <div class="fair-dialog-panel__content">
        <slot>
          <FairDialogContent
            v-if="contentText"
            :text="contentText"
          />
        </slot>
      </div>
    </template>

    <template v-else>
      <FairIconButton
        v-if="closable"
        class="fair-dialog-panel__close"
        icon="close"
        :aria-label="closeLabel"
        @click="emit('close')"
      />
      <slot>
        <FairDialogContent
          v-if="contentText"
          :text="contentText"
        />
      </slot>
    </template>

    <!-- A line over the footer — e.g. a save error (Styles D7). -->
    <slot name="notice" />

    <slot name="footer">
      <FairDialogFooter
        v-if="showFooter"
        :show-cancel="showCancel"
        :cancel-label="cancelLabel"
        :confirm-label="confirmLabel"
        :confirm-disabled="confirmDisabled"
        :confirm-loading="confirmLoading"
        :mobile="mobile"
        :cancel-leading-icon="cancelLeadingIcon"
        :cancel-trailing-icon="cancelTrailingIcon"
        :confirm-leading-icon="confirmLeadingIcon"
        :confirm-trailing-icon="confirmTrailingIcon"
        @cancel="emit('cancel', $event)"
        @confirm="emit('confirm', $event)"
      />
    </slot>
  </div>
</template>
